import { ReactNode } from "react";
import { AioIconButton } from "../additional/aioIconButton";
import { useBlockContext } from "./BlockContentProvider";
import { ADD_LINE } from "./blockReducer";
import { newBlockLine } from "./newBlockLine";

interface AddLineButtonProps {
  aifid: string;
  lineAbove?: boolean;
}

/**
 * Button to add a new line above or below the current line.
 *
 * @param props - Destructured parameter object
 * @param props.aifid - The block instance id (`aifid`) identifying which line to target.
 * @param props.lineAbove - When true, insert the new line above the target line; otherwise insert below.
 */
export const AddLineButton = ({ aifid, lineAbove = false }: AddLineButtonProps): ReactNode => {
  const ADD_LINE_BUTTON_TOP_OFFSET = -18;
  const { state, dispatch } = useBlockContext();
  const ix = state.lines.findIndex((l) => l.aifid === aifid);
  const thisLine = ix > -1 ? state.lines[ix] : null;
  const canAddLineBelow =
    !state.disabled && state.lines.length < state.maxLines && thisLine?.addBelow;

  return !thisLine || state.maxLines === 1 ? null : (
    <AioIconButton
      id={`${state.id}-${ix}-add-line`}
      iconName={lineAbove || canAddLineBelow ? "aioxPlus" : "aioxCircle"}
      tipText="Add line"
      style={
        lineAbove ? { position: "absolute", top: ADD_LINE_BUTTON_TOP_OFFSET, marginLeft: 0 } : {}
      }
      onClick={() => {
        if (canAddLineBelow || lineAbove) {
          const line = newBlockLine(state.defaultType, state.editorProps.blankT);
          line.canChangeType = state.canChangeType;
          dispatch({ operation: ADD_LINE, ix: lineAbove ? ix : ix + 1, line });
        }
      }}
    />
  );
};

AddLineButton.displayName = "AddLineButton";
